import React, { useState } from 'react'
import { Sidebar } from 'primereact/sidebar'
import { Button, Input, Spinner } from 'reactstrap'
import './LogsSidebar.scss'

const LogsSidebar = ({ 
  visible, 
  onHide, 
  logs, 
  loading, 
  onRefresh, 
  onFilterChange,
  allLogs 
}) => {
  const [expandedRows, setExpandedRows] = useState([]);
  const [sortField, setSortField] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');

  const getLogLevelClass = (eventType) => {
    const type = eventType.toLowerCase();
    switch (type) {
      case 'error':
        return 'log-level-error';
      case 'warning':
        return 'log-level-warning';
      case 'info':
        return 'log-level-info';
      case 'debug':
        return 'log-level-debug';
      default:
        return 'log-level-default';
    }
  };

  const getLogLevelIcon = (eventType) => {
    const type = eventType.toLowerCase();
    switch (type) {
      case 'error':
        return 'mdi mdi-alert-circle';
      case 'warning':
        return 'mdi mdi-alert';
      case 'info':
        return 'mdi mdi-information';
      case 'debug':
        return 'mdi mdi-bug';
      default:
        return 'mdi mdi-checkbox-blank-circle-outline';
    }
  };

  const toggleRow = (index) => {
    setExpandedRows(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return 'mdi mdi-sort';
    return sortOrder === 'asc' ? 'mdi mdi-sort-ascending' : 'mdi mdi-sort-descending';
  };

  const sortedLogs = [...logs].sort((a, b) => {
    if (sortField === 'createdAt') {
      return sortOrder === 'asc' 
        ? new Date(a.createdAt) - new Date(b.createdAt)
        : new Date(b.createdAt) - new Date(a.createdAt);
    }
    if (sortField === 'eventType') {
      return sortOrder === 'asc'
        ? a.eventType.localeCompare(b.eventType)
        : b.eventType.localeCompare(a.eventType);
    }
    if (sortField === 'message') {
      return sortOrder === 'asc'
        ? a.message.localeCompare(b.message)
        : b.message.localeCompare(a.message);
    }
    return 0;
  });

  

  return (
    <Sidebar
      header={
        <div className="d-flex align-items-center justify-content-between w-100 logs-header">
          <h3 className="m-0">Logs Viewer</h3>
          <div className="d-flex gap-2">
            <Input
              type="select"
              className="form-control-sm log-level-select"
              onChange={(e) => onFilterChange(e.target.value)}
            >
              <option value="all">All Log Levels</option>
              <option value="error">Error</option>
              <option value="warning">Warning</option>
              <option value="info">Info</option>
              <option value="debug">Debug</option>
            </Input>
            <Button
              size="sm"
              color="light"
              className="refresh-btn"
              onClick={onRefresh}
            >
              <i className="mdi mdi-refresh" />
            </Button>
          </div>
        </div>
      }
      visible={visible}
      position="right"
      onHide={onHide}
      className="logs-sidebar"
      style={{ width: '55rem' }}
    >
      <div className="logs-container">
        {loading ? (
          <div className="text-center p-4">
            <Spinner />
          </div>
        ) : (
          <div className="logs-list">
            <div className="logs-table">
              <div className="logs-header-row">
                <div className="log-cell log-level-header" onClick={() => handleSort('eventType')}>
                  Level
                  <i className={getSortIcon('eventType')} />
                </div>
                <div className="log-cell log-timestamp-header" onClick={() => handleSort('createdAt')}>
                  Timestamp
                  <i className={getSortIcon('createdAt')} />
                </div>
                <div className="log-cell log-message-header" onClick={() => handleSort('message')}>
                  Message
                  <i className={getSortIcon('message')} />
                </div>
                <div className="log-cell log-route-header" onClick={() => handleSort('route')}>
                  Route
                  <i className={getSortIcon('route')} />
                </div>

                <div className="log-cell log-actions-header"></div>
              </div>
              <div className="log-entries">
                {sortedLogs.length === 0 ? (
                  <div className="text-center text-muted p-4">
                    <i className="mdi mdi-information-outline fs-1" />
                    <p>No logs available</p>
                  </div>
                ) : (
                  sortedLogs.map((log, index) => (
                    <div key={index} className={`log-entry ${expandedRows.includes(index) ? 'expanded' : ''}`}>
                      <div 
                        className="log-entry-header"
                        onClick={() => toggleRow(index)}
                      >
                        <div className="log-cell log-level">
                          <i className={`${getLogLevelIcon(log.eventType)} ${getLogLevelClass(log.eventType)}`} />
                          <span className="log-level-text">{log.eventType}</span>
                        </div>
                        <div className="log-cell log-timestamp">
                          {new Date(log.createdAt).toLocaleString()}
                        </div>
                        <div className="log-cell log-message">
                          {log.message}
                        </div>
                        <div className="log-cell log-message">
                          {log.route}
                        </div>
                        <div className="log-cell log-entry-arrow">
                          <i className={`mdi ${expandedRows.includes(index) ? 'mdi-chevron-up' : 'mdi-chevron-down'}`} />
                        </div>
                      </div>
                      {expandedRows.includes(index) && log.metaData && (
                        <div className="log-entry-details">
                          <div className="metadata-section">
                            {/* <div className="metadata-header">
                              <span className="metadata-title">Metad</span>
                            </div> */}
                            <pre className="metadata-content">
                              {JSON.stringify({message: log.message, serviceName: log.serviceName, metaData: log.metaData}, null, 2)}
                            </pre>
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </Sidebar>
  );
};

export default LogsSidebar; 